import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

interface Provider {
  name: string;
}

interface ProviderSelectorProps {
  providers: Provider[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-family: var(--font-family-main);
  font-weight: 400;
  font-size: 14px;
  color: var(--color-text-secondary);
  margin-bottom: 4px;
`;

const InputContainer = styled.div`
  position: relative;
`;

const Input = styled.input<{ $hasDropdown: boolean }>`
  width: 100%;
  padding: 12px 16px;
  padding-right: ${props => props.$hasDropdown ? '40px' : '16px'};
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.04);
  color: var(--color-text);
  font-family: var(--font-family-main);
  font-size: 16px;
  line-height: 1.5;
  min-height: 48px;
  transition: all 0.2s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  
  &:hover:not(:disabled) {
    border-color: rgba(255, 255, 255, 0.24);
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DropdownButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.08);
    color: var(--color-primary);
  }
`;

const DropdownList = styled.div<{ $isOpen: boolean; $direction: 'up' | 'down' }>`
  position: absolute;
  ${props => props.$direction === 'down' ? 'top: 100%;' : 'bottom: 100%;'}
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: #0E1F1F;
  border: 1px solid var(--color-primary);
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  max-height: 200px;
  overflow-y: auto;
  ${props => props.$direction === 'down' ? 'margin-top: 4px;' : 'margin-bottom: 4px;'}
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => {
    if (!props.$isOpen) return props.$direction === 'down' ? 'translateY(-8px)' : 'translateY(8px)';
    return 'translateY(0)';
  }};
  transition: all 0.2s ease;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 4px 0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--color-primary);
    border-radius: 3px;
    border: 1px solid #0E1F1F;
  }
`;

const DropdownOption = styled.div<{ $isHighlighted: boolean }>`
  padding: 12px 16px;
  color: var(--color-text);
  font-family: var(--font-family-main);
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${props => props.$isHighlighted ? '#172A2A' : 'transparent'};
  
  &:hover {
    background-color: #172A2A;
  }
  
  &:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }
  
  &:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

const EmptyState = styled.div`
  padding: 12px 16px;
  color: rgba(255, 255, 255, 0.4);
  font-family: var(--font-family-main);
  font-size: 14px;
  text-align: center;
`;

const ProviderSelector: React.FC<ProviderSelectorProps> = ({
  providers,
  value,
  onChange,
  label,
  placeholder = "Введите поставщика",
  required = false,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownDirection, setDropdownDirection] = useState<'down' | 'up'>('down');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredProviders = providers.filter(provider =>
    provider.name.toLowerCase().includes(value.toLowerCase())
  );

  const calculateDropdownDirection = () => {
    if (!containerRef.current) return 'down';
    
    const rect = containerRef.current.getBoundingClientRect();
    const dropdownHeight = 200; // max-height dropdown'а
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    // Если снизу места достаточно или сверху места меньше, открываем вниз
    if (spaceBelow >= dropdownHeight || spaceAbove < dropdownHeight) {
      return 'down';
    }
    
    return 'up';
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    if (newValue && !isOpen) {
      setDropdownDirection(calculateDropdownDirection());
      setIsOpen(true);
    }
    
    setHighlightedIndex(-1);
  };

  const handleInputFocus = () => {
    if (providers.length > 0) {
      setDropdownDirection(calculateDropdownDirection());
      setIsOpen(true);
    }
  };

  const handleDropdownToggle = () => {
    if (disabled) return;
    
    if (providers.length > 0) {
      if (!isOpen) {
        setDropdownDirection(calculateDropdownDirection());
      }
      setIsOpen(!isOpen);
      if (!isOpen) {
        inputRef.current?.focus();
      }
    }
  };

  const handleProviderSelect = (providerName: string) => {
    onChange(providerName);
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled || !isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredProviders.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredProviders.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredProviders[highlightedIndex]) {
          handleProviderSelect(filteredProviders[highlightedIndex].name);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  return (
    <Container ref={containerRef}>
      {label && <Label>{label}{required && ' *'}</Label>}
      <InputContainer>
        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          $hasDropdown={providers.length > 0}
        />
        
        {providers.length > 0 && (
          <DropdownButton
            type="button"
            onClick={handleDropdownToggle}
            disabled={disabled}
          >
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </DropdownButton>
        )}
      </InputContainer>

      {providers.length > 0 && (
        <DropdownList $isOpen={isOpen} $direction={dropdownDirection}>
          {filteredProviders.length > 0 ? (
            filteredProviders.map((provider, index) => (
              <DropdownOption
                key={provider.name}
                $isHighlighted={index === highlightedIndex}
                onClick={() => handleProviderSelect(provider.name)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {provider.name}
              </DropdownOption>
            ))
          ) : value.trim() && (
            <EmptyState>
              Поставщик "{value}" будет добавлен как новый
            </EmptyState>
          )}
        </DropdownList>
      )}
    </Container>
  );
};

export default ProviderSelector; 